// @ts-nocheck
import helpers from '@/utils/helpers';
import getDefaultGameDuration from '@staty/shared/utils/getDefaultGameDuration';
import { timeToMinutes } from './timeUtils';

interface Game {
  _id?: string;
  timeSlot?: string;
  homeTeam?: string;
  awayTeam?: string;
  division: string;
  date?: string;
  globalDate?: string; // MM-DD-YYYY format
  time: string;
  location?: string;
  locationModel?: string;
  slotSectionId?: string; // Track which slot section this game belongs to
  duration?: number; // Duration in minutes
}

interface TimeSlot {
  _id: string;
  location: {
    _id: string;
    name: string;
  };
  availableDate: string;
  startTime: string;
  endTime: string;
  maxGames: number;
}

interface SlotSection {
  id: string;
  timeSlotId: string;
  game?: Game;
  divisionId: string;
  slotTime: string; // Calculated time for this specific slot
  duration?: number; // Duration in minutes for this slot section
}

interface Division {
  _id: string;
  name: string;
  sports: string[];
  config?: {
    gameDurationInMinutes?: number;
  };
}

/**
 * Calculate shared slot sections for a timeSlot (not per division) - memoized for performance
 */
export const calculateSharedSlotSections = (
  timeSlot: TimeSlot,
  displayGames: Game[],
  divisions: Division[],
  selectedDivisionIds: string[],
  pendingGames: {
    toCreate: Omit<Game, '_id'>[];
    toUpdate: Game[];
    toDelete: string[];
  },
  orgSport?: string, // organization.sports[0] — reliable sport source (division.sports mirror can be empty)
): SlotSection[] => {
  const sections: SlotSection[] = [];

  // Calculate number of sections based on actual game durations
  const startTime = new Date(`2000-01-01T${timeSlot.startTime}`);
  const endTime = new Date(`2000-01-01T${timeSlot.endTime}`);
  const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);

  // Get game duration from division config or the sport-aware default (pickleball 30, else 60)
  const firstDivision = divisions.find(d => selectedDivisionIds.includes(d._id));
  let gameDuration = firstDivision?.config?.gameDurationInMinutes || getDefaultGameDuration(orgSport ?? firstDivision?.sports?.[0]);

  const maxSections = timeSlot.maxGames ? Math.min(Math.floor(durationMinutes / gameDuration), timeSlot.maxGames)
    : Math.min(Math.floor(durationMinutes / gameDuration));

  // Combine displayGames with pending games for matching
  // Note: displayGames already filters out deleted games, so we don't need to filter again
  const allGames = [
    ...displayGames,
    ...pendingGames.toCreate.map(g => ({ ...g, _id: `temp-create-${g.slotSectionId}` })),
    ...pendingGames.toUpdate.filter(g => !pendingGames.toDelete.includes(g._id))
  ];

  // First, add all regular timeSlot sections
  for (let i = 0; i < maxSections; i++) {
    const sectionId = `${timeSlot._id}-${i}`;

    // Calculate the start time for this specific slot
    const slotStartTime = new Date(startTime.getTime() + (i * gameDuration * 60 * 1000));
    const slotTimeString = slotStartTime.toTimeString().slice(0, 5); // Format as HH:MM

    // Find any game that belongs to this slot section (across all divisions)
    let game = allGames.find(g =>
      g.timeSlot === timeSlot._id &&
      g.slotSectionId === sectionId
    );

    // Use the game's actual duration if available, otherwise use the division's default
    let actualGameDuration = gameDuration;
    if (game && game.duration) {
      actualGameDuration = game.duration;
    }

    // If no game found with timeSlot, try to match by location, time, and date
    if (!game) {
      game = allGames.find(g => {
        // Check date match (both are MM-DD-YYYY format)
        const gameDate = g.globalDate;
        const timeSlotDate = timeSlot.availableDate;
        if (gameDate !== timeSlotDate) return false;

        // Check location match
        const gameLocation = g.location;
        const gameLocationModel = g.locationModel;
        const timeSlotLocationName = timeSlot.location?.name;
        const timeSlotLocationId = timeSlot.location?._id;

        const locationMatch = gameLocation === timeSlotLocationName ||
          helpers.getObjId(gameLocationModel) === timeSlotLocationId;

        if (!locationMatch) return false;

        // Check time overlap
        const gameTime = g.time;
        const timeSlotStart = timeSlot.startTime;
        const timeSlotEnd = timeSlot.endTime;

        if (!gameTime) return false;

        // Convert times to minutes for proper comparison
        const gameTimeMinutes = timeToMinutes(gameTime);
        const timeSlotStartMinutes = timeToMinutes(timeSlotStart);
        const timeSlotEndMinutes = timeToMinutes(timeSlotEnd);

        // Check if game time matches slot section time exactly
        const slotTimeMinutes = timeToMinutes(slotTimeString);
        const matchesSlotTime = gameTimeMinutes === slotTimeMinutes;

        return matchesSlotTime;
      });
    }

    sections.push({
      id: sectionId,
      timeSlotId: timeSlot._id,
      divisionId: '', // Will be determined when a game is created
      game: game,
      slotTime: slotTimeString,
      duration: actualGameDuration, // Include the actual game duration
    });
  }

  return sections;
};

/**
 * Helper function to get timeSlots for a specific time and date
 */
export const getTimeSlotsForTimeAndDate = (
  timeSlotsByDate: Record<string, Record<string, TimeSlot[]>>,
  timeSlot: string,
  date: string,
  calculateSharedSlotSections: (timeSlot: TimeSlot) => SlotSection[]
): Array<TimeSlot & { slotSection: SlotSection }> => {
  const timeSlotsForDate = timeSlotsByDate[date] || {};
  const timeMinutes = timeToMinutes(timeSlot);

  const matchingTimeSlots = [];
  Object.values(timeSlotsForDate).flat().forEach(timeSlotObj => {
    // Calculate slot sections for this timeSlot to see if the grid time matches any section
    const slotSections = calculateSharedSlotSections(timeSlotObj);

    // Check if the grid time matches any of the slot section times
    const matchingSection = slotSections.find(section => {
      const sectionTimeMinutes = timeToMinutes(section.slotTime);
      return sectionTimeMinutes === timeMinutes;
    });

    if (matchingSection) {
      matchingTimeSlots.push({
        ...timeSlotObj,
        slotSection: matchingSection // Include the specific slot section
      });
    }
  });

  return matchingTimeSlots;
};

